import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Switch } from 'app/components/ui/switch';
import { Label } from 'app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'app/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu';
import {
  Settings,
  Mail,
  Send,
  Activity,
  Shield,
  Zap,
  MoreVertical,
  Edit,
  TestTube,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Info,
} from 'lucide-react';
import { useEmailConfigSlice } from './slice';
import { useToast } from 'app/components/ui/use-toast';
import {
  BrevoPopup,
  BrevoTestPopup,
  MailgunPopup,
  MailgunTestPopup,
  EmailStats,
  EmailLogs,
} from './components';
import { EmailProvider } from './slice/types';

const EmailConfiguration: React.FC = () => {
  const { toast } = useToast();
  const [selectedProvider, setSelectedProvider] =
    useState<EmailProvider | null>(null);
  const [selectedProviderType, setSelectedProviderType] = useState<string>('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [isBrevoTestOpen, setIsBrevoTestOpen] = useState(false);
  const [isMailgunTestOpen, setIsMailgunTestOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const {
    useGetEmailProvidersQuery,
    useAddEmailProviderMutation,
    useUpdateEmailProviderMutation,
    useDeleteEmailProviderMutation,
    useTestEmailProviderMutation,
    useToggleProviderStatusMutation,
    // Brevo specific
    useGetBrevoConfigQuery,
    useAddBrevoConfigMutation,
    useUpdateBrevoConfigMutation,
    useDeleteBrevoConfigMutation,
    useTestBrevoConfigMutation,
    useToggleBrevoStatusMutation,
    // Mailgun specific
    useGetMailgunConfigQuery,
    useAddMailgunConfigMutation,
    useUpdateMailgunConfigMutation,
    useDeleteMailgunConfigMutation,
    useTestMailgunConfigMutation,
    useToggleMailgunStatusMutation,
  } = useEmailConfigSlice();

  const [
    getEmailProviders,
    { data: providersData, isLoading: providersLoading },
  ] = useGetEmailProvidersQuery();
  const [addEmailProvider, { isLoading: addLoading }] =
    useAddEmailProviderMutation();
  const [updateEmailProvider, { isLoading: updateLoading }] =
    useUpdateEmailProviderMutation();
  const [deleteEmailProvider] = useDeleteEmailProviderMutation();
  const [testEmailProvider] = useTestEmailProviderMutation();
  const [toggleProviderStatus] = useToggleProviderStatusMutation();

  // Brevo specific hooks
  const [getBrevoConfig, { data: brevoData, isLoading: brevoLoading }] =
    useGetBrevoConfigQuery();
  const [addBrevoConfig] = useAddBrevoConfigMutation();
  const [updateBrevoConfig] = useUpdateBrevoConfigMutation();
  const [deleteBrevoConfig] = useDeleteBrevoConfigMutation();
  const [testBrevoConfig] = useTestBrevoConfigMutation();
  const [toggleBrevoStatus] = useToggleBrevoStatusMutation();

  // Mailgun specific hooks
  const [getMailgunConfig, { data: mailgunData, isLoading: mailgunLoading }] =
    useGetMailgunConfigQuery();
  const [addMailgunConfig] = useAddMailgunConfigMutation();
  const [updateMailgunConfig] = useUpdateMailgunConfigMutation();
  const [deleteMailgunConfig] = useDeleteMailgunConfigMutation();
  const [testMailgunConfig] = useTestMailgunConfigMutation();
  const [toggleMailgunStatus] = useToggleMailgunStatusMutation();

  const providers = providersData?.data || [];
  console.log(providers);

  useEffect(() => {
    getEmailProviders();
  }, []);

  // Ensure config is loaded when popup opens
  useEffect(() => {
    if (isPopupOpen && selectedProvider && selectedProviderType) {
      const loadConfig = async () => {
        try {
          if (selectedProviderType === 'brevo') {
            await getBrevoConfig();
          } else if (selectedProviderType === 'mailgun') {
            await getMailgunConfig();
          }
        } catch (error) {
          console.error('Failed to load provider config:', error);
        }
      };

      loadConfig();
    }
  }, [isPopupOpen, selectedProvider, selectedProviderType]);

  const handleEditProvider = async (provider: EmailProvider) => {
    const providerType = provider.provider_name || provider.type || 'brevo';
    setSelectedProviderType(providerType);

    // Load specific provider configuration
    if (providerType === 'brevo') {
      await getBrevoConfig();
    } else if (providerType === 'mailgun') {
      await getMailgunConfig();
    }

    setSelectedProvider(provider);
    setIsPopupOpen(true);
  };

  const handleOpenConfigurePopup = async (provider: EmailProvider) => {
    const providerType = provider.provider_name || provider.type || 'brevo';
    setSelectedProviderType(providerType);
    setSelectedProvider(provider);

    // Always load specific provider configuration when opening popup
    try {
      if (providerType === 'brevo') {
        await getBrevoConfig();
      } else if (providerType === 'mailgun') {
        await getMailgunConfig();
      }
    } catch (error) {
      console.error('Failed to load provider config:', error);
      // Continue with popup opening even if config loading fails
      // The useEffect will retry loading the config when the popup opens
    }

    // Always open the popup, regardless of config loading success
    setIsPopupOpen(true);
  };

  const handleDeleteProvider = async (
    providerId: string,
    providerType: string,
  ) => {
    if (window.confirm('Are you sure you want to delete this provider?')) {
      try {
        if (providerType === 'brevo') {
          await deleteBrevoConfig(providerId);
        } else if (providerType === 'mailgun') {
          await deleteMailgunConfig(providerId);
        } else {
          await deleteEmailProvider({ id: providerId, type: providerType });
        }
        toast({
          title: 'Provider Deleted',
          description: 'Email provider has been removed successfully.',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to delete provider.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleToggleStatus = async (
    providerId: string,
    providerType: string,
    currentStatus: boolean,
  ) => {
    try {
      if (providerType === 'brevo') {
        await toggleBrevoStatus({ id: providerId, isActive: !currentStatus });
      } else if (providerType === 'mailgun') {
        await toggleMailgunStatus({ id: providerId, isActive: !currentStatus });
      } else {
        await toggleProviderStatus({
          id: providerId,
          type: providerType,
          isActive: !currentStatus,
        });
      }
      toast({
        title: 'Status Updated',
        description: 'Provider status has been updated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update provider status.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveProvider = async (provider: EmailProvider) => {
    try {
      // Map form data to API structure
      const apiProvider = {
        ...provider,
        provider_name: provider.type || 'brevo',
        provider_display_name: provider.name,
        sender_name: provider.config?.senderName || '',
        sender_email: provider.config?.senderEmail || '',
        is_active: provider.isActive ? 1 : 0,
        is_default: 0,
        priority: 1,
      };

      if (selectedProvider) {
        await updateEmailProvider({
          id: provider.id,
          data: apiProvider,
        });
      } else {
        await addEmailProvider(apiProvider);
      }
      setIsPopupOpen(false);
      toast({
        title: 'Provider Saved',
        description: 'Email provider configuration saved successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save provider configuration.',
        variant: 'destructive',
      });
    }
  };

  // Provider-specific save handlers
  const handleSaveBrevoConfig = async (config: any) => {
    try {
      // If this is a new activation (no config.id), automatically activate the provider
      const configToSave = config.id ? config : { ...config, is_active: 1 };

      if (configToSave.id) {
        await updateBrevoConfig({ id: configToSave.id, data: configToSave });
      } else {
        await addBrevoConfig(configToSave);
      }
      setIsPopupOpen(false);
      toast({
        title: config.id
          ? 'Brevo Configuration Updated'
          : 'Brevo Configuration Activated',
        description: config.id
          ? 'Brevo configuration updated successfully.'
          : 'Brevo configuration activated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save Brevo configuration.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveMailgunConfig = async (config: any) => {
    try {
      // If this is a new activation (no config.id), automatically activate the provider
      const configToSave = config.id ? config : { ...config, is_active: 1 };

      if (configToSave.id) {
        await updateMailgunConfig({ id: configToSave.id, data: configToSave });
      } else {
        await addMailgunConfig(configToSave);
      }
      setIsPopupOpen(false);
      toast({
        title: config.id
          ? 'Mailgun Configuration Updated'
          : 'Mailgun Configuration Activated',
        description: config.id
          ? 'Mailgun configuration updated successfully.'
          : 'Mailgun configuration activated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save Mailgun configuration.',
        variant: 'destructive',
      });
    }
  };

  const handleSendTestEmail = async (testData: any) => {
    const providerType = selectedProviderType;

    if (providerType === 'brevo') {
      return await testBrevoConfig({
        id: selectedProvider!.id,
        testData,
      });
    } else if (providerType === 'mailgun') {
      return await testMailgunConfig({
        id: selectedProvider!.id,
        testData,
      });
    } else {
      return await testEmailProvider({
        id: selectedProvider!.id,
        type: providerType,
        testData,
      });
    }
  };

  const handleOpenTestPopup = (provider: EmailProvider) => {
    const providerType = provider.provider_name || provider.type || 'brevo';
    setSelectedProviderType(providerType);
    setSelectedProvider(provider);

    if (providerType === 'brevo') {
      setIsBrevoTestOpen(true);
    } else if (providerType === 'mailgun') {
      setIsMailgunTestOpen(true);
    }
  };

  const handleShowProviderInfo = (provider: EmailProvider) => {
    setSelectedProvider(provider);
    setSelectedProviderType(provider.provider_name || provider.type || 'brevo');
    setIsInfoOpen(true);
  };

  const handleGetEmailLogs = async (provider: string) => {
    // This would call the appropriate logs API based on provider
    console.log(`Getting logs for ${provider}`);
    return { data: [] };
  };

  if (providersLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading email providers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Email Configuration
          </h1>
          <p className="text-muted-foreground">
            Manage your email service providers for transactional emails and
            marketing campaigns.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={isStatsOpen} onOpenChange={setIsStatsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Activity className="h-4 w-4 mr-2" />
                View Analytics
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Email Analytics</DialogTitle>
                <DialogDescription>
                  Monitor your email delivery performance and engagement
                  metrics.
                </DialogDescription>
              </DialogHeader>
              <EmailStats />
            </DialogContent>
          </Dialog>

          <Dialog open={isLogsOpen} onOpenChange={setIsLogsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Settings className="h-4 w-4 mr-2" />
                View Logs
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Email Logs</DialogTitle>
                <DialogDescription>
                  Monitor email delivery status and troubleshoot issues.
                </DialogDescription>
              </DialogHeader>
              <EmailLogs />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Providers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {providersData?.data?.map(provider => (
          <Card key={provider.id} className="relative max-w-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-gray-50 mr-2">
                    <img
                      src={`/images/${provider.provider_name}.svg`}
                      alt={provider.provider_display_name}
                      className="h-12 w-20 object-contain"
                      onError={e => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="absolute top-0 right-0">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base truncate">
                      {provider.provider_display_name}
                    </CardTitle>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`status-${provider.id}`}
                        checked={!!provider.is_active}
                        onCheckedChange={() =>
                          handleToggleStatus(
                            provider.id,
                            provider.provider_name || provider.type || 'brevo',
                            !!provider.is_active,
                          )
                        }
                      />
                      <span className="text-xs font-medium">
                        {provider.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Provider Description */}
              <div className="text-xs text-muted-foreground leading-relaxed">
                {provider.provider_name === 'brevo' ? (
                  <>
                    <p>
                      Formerly Sendinblue, offers powerful transactional emails
                    </p>
                    <p>
                      and marketing campaigns with excellent deliverability.
                    </p>
                    <a
                      href="https://www.brevo.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                    >
                      Visit Brevo.com →
                    </a>
                  </>
                ) : provider.provider_name === 'mailgun' ? (
                  <>
                    <p>Developer-focused email API with advanced features</p>
                    <p>and superior deliverability for high-volume sending.</p>
                    <a
                      href="https://www.mailgun.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                    >
                      Visit Mailgun.com →
                    </a>
                  </>
                ) : (
                  <>
                    <p>Professional email service provider with</p>
                    <p>reliable delivery and comprehensive features.</p>
                  </>
                )}
              </div>

              {/* Actions */}
              {provider.is_active === 1 ? (
                <div className="pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenTestPopup(provider)}
                      className="flex-1 h-8 text-xs"
                    >
                      <TestTube className="h-3 w-3 mr-1" />
                      Send Test Email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsInfoOpen(true)}
                      className="flex-1 h-8 text-xs"
                    >
                      <Info className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => handleOpenConfigurePopup(provider)}
                    className="flex-1 h-8 text-xs w-full bg-primary text-white mt-2"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Configure
                  </Button>
                </div>
              ) : (
                <div className="pt-3 border-t">
                  <Button
                    size="sm"
                    onClick={() => handleOpenConfigurePopup(provider)}
                    className="flex-1 h-8 text-xs w-full bg-primary text-white mt-2"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Activate
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Provider Dialog */}
      <Dialog open={isPopupOpen} onOpenChange={setIsPopupOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedProvider
                ? `Edit ${
                    selectedProviderType === 'brevo'
                      ? 'Brevo'
                      : selectedProviderType === 'mailgun'
                      ? 'Mailgun'
                      : 'Provider'
                  } Configuration`
                : `Activate ${
                    selectedProviderType === 'brevo'
                      ? 'Brevo'
                      : selectedProviderType === 'mailgun'
                      ? 'Mailgun'
                      : 'Provider'
                  } Configuration`}
            </DialogTitle>
            <DialogDescription>
              {selectedProvider
                ? `Configure your ${
                    selectedProviderType === 'brevo'
                      ? 'Brevo (Sendinblue)'
                      : selectedProviderType === 'mailgun'
                      ? 'Mailgun'
                      : 'email service provider'
                  } settings.`
                : `Activate and configure your ${
                    selectedProviderType === 'brevo'
                      ? 'Brevo (Sendinblue)'
                      : selectedProviderType === 'mailgun'
                      ? 'Mailgun'
                      : 'email service provider'
                  } settings.`}
            </DialogDescription>
          </DialogHeader>
          {selectedProviderType === 'brevo' && (
            <BrevoPopup
              isOpen={isPopupOpen}
              onClose={() => setIsPopupOpen(false)}
              config={brevoData?.data || null}
              isLoading={brevoLoading}
            />
          )}
          {selectedProviderType === 'mailgun' && (
            <MailgunPopup
              isOpen={isPopupOpen}
              onClose={() => setIsPopupOpen(false)}
              config={mailgunData?.data || null}
              onSave={handleSaveMailgunConfig}
              isLoading={mailgunLoading}
            />
          )}
          {!selectedProviderType && (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading configuration...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Popups */}
      <BrevoTestPopup
        isOpen={isBrevoTestOpen}
        onClose={() => setIsBrevoTestOpen(false)}
        onSendTest={handleSendTestEmail}
      />

      <MailgunTestPopup
        isOpen={isMailgunTestOpen}
        onClose={() => setIsMailgunTestOpen(false)}
        onSendTest={handleSendTestEmail}
      />

      {/* Info Popup */}
      <Dialog open={isInfoOpen} onOpenChange={setIsInfoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gray-500 text-white">
                <Info className="h-5 w-5" />
              </div>
              <div>
                <div className="text-lg font-bold">Provider Information</div>
                <DialogDescription>
                  {selectedProvider?.provider_display_name} details
                </DialogDescription>
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedProvider && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Provider</p>
                  <p className="font-medium">
                    {selectedProvider.provider_display_name}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <p className="font-medium">
                    {selectedProvider.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Sender Name</p>
                  <p className="font-medium">
                    {selectedProvider.sender_name || 'Not configured'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Sender Email</p>
                  <p className="font-medium">
                    {selectedProvider.sender_email || 'Not configured'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Priority</p>
                  <p className="font-medium">{selectedProvider.priority}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Default</p>
                  <p className="font-medium">
                    {selectedProvider.is_default ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsInfoOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailConfiguration;
